/**
 * UiProvider — wraps the whole app once (in containers/App.js).
 *
 * What it sets up:
 *   - Ant Design theme from theme.js (colors, font, radius, motion)
 *   - Right-to-left layout + Hebrew locale (date pickers, empty states, pagination)
 *   - Ant's <App> so message/modal/notification pick up the theme
 *   - The same theme for Ant's *static* calls (message.info(), Modal.confirm())
 *     which Redux actions use outside React (see actions/generalActions.js)
 *   - Global CSS (ui.css)
 *
 * Usage:
 *   <UiProvider><Everything /></UiProvider>
 */
import React from 'react';
import { ConfigProvider, App as AntApp } from 'antd';
import heIL from 'antd/locale/he_IL';
import dayjs from 'dayjs';
import 'dayjs/locale/he';

import { antTheme } from './theme';
import './ui.css';

dayjs.locale('he');

// Load the Heebo UI font (theme.js fontFamily). Injected here rather than in
// public/index.html, which is kept read-only in this repo.
const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Heebo:wght@400;500;600;700&display=swap';
if (typeof document !== 'undefined' && !document.querySelector(`link[href="${FONT_HREF}"]`)) {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = FONT_HREF;
  document.head.appendChild(link);
}

// Static Ant calls (message.info, Modal.confirm) render outside the React tree,
// so they need the provider config handed to them explicitly.
ConfigProvider.config({
  holderRender: (children) => (
    <ConfigProvider direction="rtl" locale={heIL} theme={antTheme}>
      {children}
    </ConfigProvider>
  ),
});

const UiProvider = ({ children }) => (
  <ConfigProvider direction="rtl" locale={heIL} theme={antTheme}>
    <AntApp>{children}</AntApp>
  </ConfigProvider>
);

export default UiProvider;
